/**
 * Pointer-driven drag and drop, so it works with a mouse and on touch screens alike.
 * Touch drags start with a long press, which leaves a quick swipe free to scroll the page.
 */

interface DragOptions {
  root: HTMLElement;
  /** Selector for the draggable elements. Each carries its id in `data-drag`. */
  source: string;
  /** Selector for the drop zones. */
  zone: string;
  /** Image that follows the pointer. */
  ghost: (id: string) => string;
  /** `zone` is null when dropped outside every zone; `hit` is the element under the pointer. */
  drop: (id: string, zone: HTMLElement | null, hit: Element | null) => void;
}

const MOVE_THRESHOLD = 6;
const LONG_PRESS_MS = 250;

const attach = ({ root, source, zone, ghost, drop }: DragOptions): void => {
  root.addEventListener('contextmenu', e => {
    if (e.target instanceof Element && e.target.closest(source)) e.preventDefault();
  });
  root.addEventListener('pointerdown', down => {
    const item = down.target instanceof Element ? down.target.closest<HTMLElement>(source) : null;
    const id = item?.dataset.drag;
    if (!item || !id || down.button !== 0) return;
    const isTouch = down.pointerType !== 'mouse';
    let ready = !isTouch;
    let img: HTMLImageElement | null = null;
    let over: HTMLElement | null = null;

    const pressTimer = isTouch
      ? window.setTimeout(() => {
          ready = true;
          item.classList.add('pressed');
          navigator.vibrate?.(10);
        }, LONG_PRESS_MS)
      : 0;

    const hitAt = (e: PointerEvent): Element | null => document.elementFromPoint(e.clientX, e.clientY);
    const distance = (e: PointerEvent): number => Math.hypot(e.clientX - down.clientX, e.clientY - down.clientY);
    const place = (e: PointerEvent) => {
      if (!img) return;
      img.style.left = `${e.clientX}px`;
      img.style.top = `${e.clientY}px`;
    };
    const start = (e: PointerEvent) => {
      img = document.createElement('img');
      img.className = 'drag-ghost';
      img.src = ghost(id);
      document.body.append(img);
      document.body.classList.add('dragging');
      item.classList.add('drag-source');
      place(e);
    };
    const move = (e: PointerEvent) => {
      if (!ready) {
        if (distance(e) > MOVE_THRESHOLD) finish();
        return;
      }
      if (!img) {
        if (!isTouch && distance(e) < MOVE_THRESHOLD) return;
        start(e);
      }
      place(e);
      const next = hitAt(e)?.closest<HTMLElement>(zone) ?? null;
      if (next === over) return;
      over?.classList.remove('drop-over');
      next?.classList.add('drop-over');
      over = next;
    };
    const blockScroll = (e: TouchEvent) => {
      if (ready) e.preventDefault();
    };
    const up = (e: PointerEvent) => {
      const dropped = !!img;
      finish();
      if (!dropped) return;
      const hit = hitAt(e);
      drop(id, hit?.closest<HTMLElement>(zone) ?? null, hit);
    };
    const finish = () => {
      clearTimeout(pressTimer);
      img?.remove();
      over?.classList.remove('drop-over');
      document.body.classList.remove('dragging');
      item.classList.remove('drag-source', 'pressed');
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', finish);
      window.removeEventListener('touchmove', blockScroll);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', finish);
    window.addEventListener('touchmove', blockScroll, { passive: false });
  });
};

export const Drag = { attach };
